import { versionKeys } from "@features/versions/services/queryKeys";
import type { ConnectorAnnotationInput, Project } from "@models/project";
import { toast } from "@stores/toastStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectsApi } from "../services/api";
import { projectKeys } from "../services/queryKeys";
import { markRecentlyQueued } from "../utils/datasheetStatus";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * List projects in the current scope.
 *
 *   organisationId === undefined  → default scope (personal + all orgs).
 *   organisationId === null or "" → same as undefined.
 *   organisationId === "personal" → personal only.
 *   organisationId === <uuid>     → that org only.
 *
 * The OrgSwitcher passes `activeOrgId ?? "personal"` so the active context
 * determines which projects appear — "Personal" shows only personal, any
 * specific org shows only that org's projects.
 */
export function useProjects(organisationId?: string | null) {
  return useQuery({
    queryKey: projectKeys.lists(organisationId),
    queryFn: () => projectsApi.list(organisationId || undefined),
    // Auto-refresh the list while any board is still enriching, so
    // the per-board enrichment chip updates without the user
    // refreshing. Polls every 5 s ONLY while in flight — once every
    // board has settled (`status` ∈ {none, partial, done}) the
    // interval returns `false` and the query goes idle. Same pattern
    // as `useExtractionStatus` below.
    refetchInterval: (query) => {
      const projects = query.state.data;
      if (!Array.isArray(projects) || projects.length === 0) {
        return false;
      }
      const hasInflight = projects.some(
        (p) =>
          Array.isArray(p?.boards) &&
          p.boards.some((b) => b?.enrichment?.status === "in_progress"),
      );
      return hasInflight ? 5000 : false;
    },
  });
}

/**
 * Single-project lookup that shares the {@link useProjects} cache.
 *
 * `select` runs the find against the cached list once per cache update, so
 * subscribers only re-render when *their* project changes — not every time
 * an unrelated project on the same list reshapes. Without this, ten
 * project-context surfaces each re-render on every list mutation.
 *
 * Returns `undefined` when the list is still loading or the project isn't in
 * scope; callers should guard accordingly. The underlying list query is
 * reused even if the caller doesn't pin a specific `organisationId` — pass
 * the same scope the parent surface uses to hit the same cache entry.
 */
export function useProject(
  projectId: string | null | undefined,
  organisationId?: string | null,
) {
  return useQuery({
    queryKey: projectKeys.lists(organisationId),
    queryFn: () => projectsApi.list(organisationId || undefined),
    enabled: !!projectId,
    select: (list: Project[]) =>
      projectId ? list.find((p) => p.id === projectId) : undefined,
  });
}

export function useProjectStats(
  id: string,
  enabled = true,
  versionId?: string | null,
  organisationId?: string | null,
) {
  return useQuery({
    queryKey: projectKeys.stats(id, versionId, organisationId),
    queryFn: () => projectsApi.getStats(id, versionId, organisationId),
    enabled: enabled && !!id,
  });
}

export function useExtractionStatus(
  id: string,
  enabled = false,
  versionId?: string | null,
  organisationId?: string | null,
) {
  return useQuery({
    queryKey: projectKeys.extractionStatus(id, versionId, organisationId),
    queryFn: () =>
      projectsApi.getExtractionStatus(id, versionId, organisationId),
    enabled: enabled && !!id,
    // Poll every 5 s only while at least one MPN is genuinely in flight
    // ("extracting" / "pending"). "missing" / "failed" parts never flip
    // on their own — the user has to click Extract — so polling on
    // them just burns network. `useExtractDatasheet.onSuccess`
    // invalidates this query explicitly, which triggers a fresh fetch
    // and resumes polling the moment a new extraction is queued.
    refetchInterval: (query) => {
      if (!enabled) return false;
      const data = query.state.data;
      const partsMap = data?.parts;
      if (!partsMap || typeof partsMap !== "object") return 5000;
      const hasInflight = Object.values(partsMap).some(
        (s) => s === "extracting" || s === "pending",
      );
      return hasInflight ? 5000 : false;
    },
  });
}

export function useProjectGraph(
  id: string,
  enabled = true,
  versionId?: string | null,
  organisationId?: string | null,
) {
  return useQuery({
    queryKey: projectKeys.graph(id, versionId, organisationId),
    queryFn: () => projectsApi.getGraph(id, versionId, organisationId),
    enabled: enabled && !!id,
  });
}

export function useComponentDatasheet(
  id: string,
  ref: string | null,
  enabled = true,
  versionId?: string | null,
  organisationId?: string | null,
) {
  return useQuery({
    queryKey: projectKeys.componentDatasheet(
      id,
      ref ?? "",
      versionId,
      organisationId,
    ),
    queryFn: () =>
      projectsApi.getComponentDatasheet(
        id,
        ref ?? "",
        versionId,
        organisationId,
      ),
    enabled: enabled && !!id && !!ref,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mint an empty project (no netlist). Used by the "+ New project"
 * gesture on the empty-state CTA and the projects-list toolbar so
 * users can spin up a project from a name + optional brief alone —
 * uploads happen later (or never, if the design lives entirely in chat).
 */
export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: projectsApi.create,
    onSuccess: () => {
      // New project could land in any scope — invalidate every list cache.
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not create project"));
    },
  });
}

/**
 * Delete a Board from its parent Project. Cascades versions +
 * components + nets + archived source files server-side; the wrapper
 * Project survives.
 */
export function useDeleteBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      boardId,
    }: {
      projectId: string;
      boardId: string;
      name?: string;
    }) => projectsApi.deleteBoard(projectId, boardId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
      const name = vars.name?.trim() || null;
      toast.success(name ? `Deleted board '${name}'` : "Board deleted");
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not delete board"));
    },
  });
}

/**
 * Add an empty Board to an existing wrapper Project.
 *
 * On success the project list is invalidated so every consumer
 * (sidebar board picker, surface nav, project card boards array)
 * re-renders with the new board immediately.
 */
export function useAddBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      name,
      descriptionMd,
    }: {
      projectId: string;
      name: string;
      descriptionMd?: string | null;
    }) => projectsApi.addBoard(projectId, { name, descriptionMd }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not add board"));
    },
  });
}

export function useUploadProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: projectsApi.upload,
    onSuccess: (data) => {
      // Nuke every project list cache — the new project could have landed
      // in any scope, and React Query's partial key match invalidates
      // nested keys too.
      void qc.invalidateQueries({ queryKey: projectKeys.all });
      if (data.project_id) {
        void qc.invalidateQueries({
          queryKey: versionKeys.byProject(data.project_id),
        });
      }
    },
  });
}

/**
 * Queue extraction for a single MPN. Used by the per-row "Extract"
 * button in the BOM tab.
 *
 * Optimistic cache update
 * -----------------------
 * On click we immediately stamp the MPN as `extracting` in the
 * extractionStatus cache. The previous version held this optimistic
 * flag in `BomRow`'s component state — which got blown away the
 * moment the row scrolled out of view and `useVirtualizer` unmounted
 * it, leaving the user staring at the row's pre-click status when
 * they scrolled back up. Putting the optimistic write on the query
 * cache keeps it alive across mount cycles AND across the BOM table /
 * detail-panel views (both subscribe to the same cache key).
 *
 * The backend may return HTTP 200 with `status: "error"` when the MCP
 * call to SheetsData failed (missing API key, MCP server unreachable,
 * MPN rejected upstream). React Query treats HTTP 200 as a success by
 * default, so we promote the in-band error to a thrown exception so
 * `onError` fires and rolls the optimistic write back.
 */
export function useExtractDatasheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      mpn,
      organisationId,
    }: {
      projectId: string;
      mpn: string;
      organisationId?: string | null;
    }) => {
      const result = await projectsApi.extractDatasheet(
        projectId,
        mpn,
        organisationId,
      );
      if (result.status === "error") {
        throw new Error(
          result.message || "MCP rejected the extraction request",
        );
      }
      return result;
    },
    onMutate: async (vars) => {
      // Stamp the recently-queued tracker so subsequent polling
      // refetches mask SheetsData's queue-propagation lag (~10–20 s
      // window where `check_extraction_status` still says
      // `not_extracted` for an MPN that just got `{status: "queued"}`
      // back from `prefetch_datasheets`). See datasheetStatus.ts.
      markRecentlyQueued(vars.mpn);

      const key = projectKeys.extractionStatus(
        vars.projectId,
        undefined,
        vars.organisationId,
      );
      // Cancel in-flight refetches so they don't clobber the
      // optimistic write before the real status converges.
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<{
        status?: string;
        progress?: number | null;
        parts?: Record<string, unknown> | null;
      }>(key);
      qc.setQueryData(key, (old: typeof previous) => {
        if (!old) return old;
        const partsCopy: Record<string, unknown> = {
          ...((old.parts ?? {}) as Record<string, unknown>),
          [vars.mpn]: "extracting",
        };
        return { ...old, status: "extracting", parts: partsCopy };
      });
      return { previous, key };
    },
    onError: (err, _vars, ctx) => {
      // Roll the optimistic write back so the row doesn't get stuck
      // on a "Extracting…" hourglass that won't resolve.
      if (ctx && ctx.previous !== undefined) {
        qc.setQueryData(ctx.key, ctx.previous);
      }
      toast.error(errorMessage(err, "Could not queue datasheet extraction"));
    },
    // Deliberately NO `onSettled` invalidation. SheetsData typically
    // takes a beat to move a freshly queued MPN out of `not_extracted`
    // into `pending` / `extracting`, and an immediate refetch races
    // ahead of that propagation — overwriting the optimistic
    // `extracting` write with `not_extracted` and bouncing the UI back
    // to the Extract button. The optimistic write keeps the row in
    // `extracting` state, which means `useExtractionStatus`'s
    // refetch-while-inflight rule keeps polling alive at 5 s tick.
    // The next tick picks up the real SheetsData status once the
    // queue has propagated, so the natural polling cadence does the
    // job without an immediate refetch.
  });
}

export function usePrefetchDatasheets() {
  return useMutation({
    mutationFn: (
      arg:
        | string
        | {
            projectId: string;
            versionId?: string | null;
            organisationId?: string | null;
          },
    ) => {
      if (typeof arg === "string") {
        return projectsApi.prefetchDatasheets(arg);
      }
      return projectsApi.prefetchDatasheets(
        arg.projectId,
        arg.versionId,
        arg.organisationId,
      );
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not queue datasheet prefetch"));
    },
  });
}

/**
 * Update the project's high-level markdown description. Empty string
 * clears it. Invalidates project list caches so the new description
 * shows up on the next read.
 */
export function useUpdateProjectBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      briefMd,
      organisationId: _organisationId,
    }: {
      projectId: string;
      briefMd: string;
      organisationId?: string | null;
    }) => projectsApi.updateBrief(projectId, briefMd, _organisationId ?? null),
    onSuccess: () => {
      // Description is part of the project list payload — refresh
      // every cached list so the description editor's "saved" state
      // and any future reads reflect the new value.
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not save project description"));
    },
  });
}

/**
 * Replace the structured per-connector annotations for a board.
 *
 * Always routes through the per-board endpoint when a `boardId` is
 * supplied — required for multi-board projects (the project-level
 * legacy endpoint 409s with 'Project … has N boards' for multi-board
 * wrappers). Falls back to the project-level endpoint only when no
 * boardId is provided, which keeps the legacy single-board callers
 * working without modification.
 *
 * The full annotation list is sent — server applies atomically.
 * Annotations omitted from the list are deleted; pass `[]` to clear.
 * Invalidates project list caches on success.
 */
export function useUpdateConnectorAnnotations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      boardId,
      annotations,
      organisationId,
    }: {
      projectId: string;
      boardId?: string | null;
      annotations: ConnectorAnnotationInput[];
      organisationId?: string | null;
    }) =>
      boardId
        ? projectsApi.updateBoardConnectorAnnotations(
            projectId,
            boardId,
            annotations,
            organisationId ?? null,
          )
        : projectsApi.updateConnectorAnnotations(
            projectId,
            annotations,
            organisationId ?? null,
          ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not save connector annotations"));
    },
  });
}

/**
 * Delete a project. Accepts either just an id (personal shortcut) or an
 * object form carrying the project's organisation_id so we hit the right
 * scope. React Query invalidation is scope-agnostic — we nuke every
 * project list cache on success.
 */
/**
 * Rename a wrapper Project. Server enforces uniqueness within scope
 * and 409s on conflict — the toast surfaces the conflict message.
 */
export function useRenameProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      projectsApi.rename(id, name),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success(`Renamed to '${vars.name}'`);
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not rename project"));
    },
  });
}

/**
 * Archive / unarchive a wrapper Project.
 *
 * Single mutation that handles both directions (target = "archived"
 * archives, "active" unarchives) so callers don't have to wire two
 * mutations into the same kebab. Toast carries the human name and the
 * action verb so the user gets a clear "Archived 'X'" / "Restored 'X'"
 * confirmation.
 */
export function useToggleProjectArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      target,
    }: {
      id: string;
      name?: string;
      target: "active" | "archived";
    }) =>
      target === "archived"
        ? projectsApi.archive(id)
        : projectsApi.unarchive(id),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
      const name = vars.name?.trim() || null;
      const verb = vars.target === "archived" ? "Archived" : "Restored";
      toast.success(name ? `${verb} '${name}'` : `${verb}`);
    },
    onError: (err, vars) => {
      const fallback =
        vars.target === "archived"
          ? "Could not archive project"
          : "Could not restore project";
      toast.error(errorMessage(err, fallback));
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      arg:
        | string
        | { id: string; name?: string; organisationId?: string | null },
    ) => {
      if (typeof arg === "string") {
        return projectsApi.delete(arg);
      }
      return projectsApi.delete(arg.id, arg.organisationId);
    },
    onSuccess: (_data, arg) => {
      const id = typeof arg === "string" ? arg : arg.id;
      // Purge the deleted project's per-project caches so the
      // briefly-still-mounted card / detail panel doesn't refetch
      // them and 404. Then invalidate the lists so the new project
      // count + cards reflect the deletion. Targeted remove +
      // selective invalidate beats the previous "invalidate all
      // project queries" — that path forced every project (not just
      // the deleted one) to refetch.
      qc.removeQueries({
        predicate: (q) => {
          const key = q.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === "projects" &&
            key[1] !== "list" &&
            key.includes(id)
          );
        },
      });
      void qc.invalidateQueries({ queryKey: projectKeys.all });
      // Prefer the human name when the caller passed it. Falls back to a
      // generic message so the toast is never useful for ops only —
      // string-form callers (legacy) and object-form callers without a
      // name still get a confirming toast, just without the project name.
      const name = typeof arg === "string" ? null : arg.name?.trim() || null;
      toast.success(name ? `Deleted '${name}'` : "Project deleted");
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not delete project"));
    },
  });
}

// `useSystems` and friends were removed in the project/board refactor
// (`docs/project-board-model.md`). Multi-board projects now expose
// their boards via `Project.boards`; the wrapper Project's
// `brief_md`, `boards`, and `cables` fields replace what `:System`
// used to carry. The Cables surface is the user-facing editor for the
// cross-board interconnect that used to live on `/systems/$name`.
